import * as React from "react";
import { formatNumber, formatMessage } from "lib/globalize-util";
import { ProductCollectionTileUnitary } from "./product-collection-tile-unitary";
import { ITileSettings, ITileSettingsValue } from "components/product-collection/manage-tile-view/types";
import classNames from "classnames";
import { DATATEST } from "constants/integration";

export interface IProductCartTileTotalsProps {
    totalCost: number;
    totalDoors: number;
    totalUnits: number;
    totalRetail: number;
    wholesalePrice: string;
    retailPrice: string;
    tileSettings: ITileSettings;
    tileSettingsValue: ITileSettingsValue;
    dimensionFilters?: string[][];
}

const ProductCartTileTotals: React.FunctionComponent<IProductCartTileTotalsProps> = ({
    totalCost,
    totalDoors,
    totalUnits,
    totalRetail,
    wholesalePrice,
    retailPrice,
    tileSettings,
    tileSettingsValue,
    dimensionFilters,
}) => {
    const isAllTileSettingsUndefined =
        tileSettings &&
        Object.keys(tileSettings)?.every?.((line) => tileSettingsValue?.[tileSettings?.[line]] === undefined);
    const thereAreNotLines = isAllTileSettingsUndefined && !tileSettings.showColorSwatch;

    const hasOnlyOneTotal =
        (tileSettings.showTotalCost && !tileSettings.showTotalRetail) ||
        (!tileSettings.showTotalCost && tileSettings.showTotalRetail);
    const hasTotals = tileSettings.showTotalCost && tileSettings.showTotalRetail;

    // if labels exists, it means the parent wants to force render this component and not show tile unitary component. Example - tile view from legacy cart.
    const forceRenderCartTileTotal = !!tileSettings.totalCostLabel || !!tileSettings.totalRetailLabel;

    const totalCostLabelLocalized =
        tileSettings.totalCostLabel ?? formatMessage(dimensionFilters.length ? "Door Cost" : "Total Cost");
    const totalRetailLabelLocalized =
        tileSettings.totalRetailLabel ?? formatMessage(dimensionFilters.length ? "Door Retail" : "Total Retail");

    const renderComponent = () => {
        if (totalUnits > 0 || forceRenderCartTileTotal) {
            return (
                <dl>
                    {(tileSettings.showUnits || tileSettings.showDoors) && (
                        <div>
                            <dt data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.PRODUCT.UNITS_DOORS}>
                                {tileSettings.showUnits && formatMessage("Units")}
                                {tileSettings.showUnits && tileSettings.showDoors && " / "}
                                {tileSettings.showDoors && formatMessage("Doors")}:
                            </dt>
                            <dd>
                                {tileSettings.showUnits && <strong>{formatNumber(totalUnits)}</strong>}
                                {tileSettings.showDoors && !!totalDoors && (
                                    <span>
                                        {tileSettings.showUnits && "/"} {formatNumber(totalDoors)}
                                    </span>
                                )}
                            </dd>
                        </div>
                    )}
                    {tileSettings.showTotalCost && (
                        <div>
                            <dt data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.PRODUCT.TOTAL_COST}>
                                {totalCostLabelLocalized}:
                            </dt>
                            <dd>
                                <strong>{totalCost}</strong>
                            </dd>
                        </div>
                    )}
                    {tileSettings.showTotalRetail && (
                        <div>
                            <dt data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.PRODUCT.TOTAL_RETAIL}>
                                {totalRetailLabelLocalized}:
                            </dt>
                            <dd>
                                <strong>{totalRetail}</strong>
                            </dd>
                        </div>
                    )}
                </dl>
            );
        } else {
            return (
                <ProductCollectionTileUnitary
                    tileSettings={tileSettings}
                    costPrice={wholesalePrice}
                    retailPrice={retailPrice}
                />
            );
        }
    };

    return (
        <div
            className={classNames("cartTotals", {
                thereAreNotLines: thereAreNotLines,
                hasDoors: tileSettings.showEachesOnHover && totalUnits > 0,
                hasOnlyTotals: hasTotals && !tileSettings.showUnits && !tileSettings.showDoors,
                hasOneTotalAndOneUnit: hasOnlyOneTotal && (tileSettings.showUnits || tileSettings.showDoors),
                hasTotalsAndOneUnit: hasTotals && (tileSettings.showUnits || tileSettings.showDoors),
                hasOnlyOneTotal: hasOnlyOneTotal,
                hasOnlyOneUnit:
                    (tileSettings.showUnits && !tileSettings.showDoors) ||
                    (!tileSettings.showUnits && tileSettings.showDoors),
                hasOnlyUnits:
                    !tileSettings.showTotalCost &&
                    !tileSettings.showTotalRetail &&
                    tileSettings.showUnits &&
                    tileSettings.showDoors,
            })}
        >
            {renderComponent()}
        </div>
    );
};

export { ProductCartTileTotals };
